import React, { useState } from 'react'
import { Formik, Form, FormikHelpers } from 'formik'
import { validateOnboardingStep1, validateOnboardingStep2 } from './validation'
import { FormInner } from './FormInner'
import { ApiOnboardingSurveyRequestBody } from 'BackendApi'
import { Button } from '@components/Basic/ButtonNbe'
import { PageEditor } from '@app/layouts/PageEditor'
import { Panel } from '@components/Panel'

interface OnboardingFormProps {
  initialValues: ApiOnboardingSurveyRequestBody
  onSubmit: (
    formValues: ApiOnboardingSurveyRequestBody,
    formikHelpers: FormikHelpers<ApiOnboardingSurveyRequestBody>
  ) => void
  isSavingApi?: boolean
  calendlyStep?: boolean
  calendlyUrl?: string
  onClickFinish?: () => void
}

export const OnboardingForm: React.FC<OnboardingFormProps> = ({
  initialValues,
  onSubmit,
  isSavingApi = false,
  onClickFinish,
  calendlyStep,
  calendlyUrl,
}) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1)
  const [surveySubmitted, setSurveySubmitted] = useState(false)
  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validateOnMount={false}
      onSubmit={(values, formikHelpers) => {
        if (currentStep === 1) {
          const isStep1Valid = validateOnboardingStep1(values)

          if (isStep1Valid) {
            setCurrentStep(2)
          }
        } else {
          if (!surveySubmitted) {
            onSubmit(values, formikHelpers)
            setSurveySubmitted(true)
          } else {
            onClickFinish?.()
          }
        }
      }}
    >
      {(formikProps) => {
        const step1Valid = validateOnboardingStep1(formikProps.values)
        const step2Valid = validateOnboardingStep2(formikProps.values)

        const cannotSubmit =
          currentStep === 1
            ? formikProps.isSubmitting || !step1Valid || isSavingApi
            : !step2Valid || isSavingApi

        return (
          <PageEditor hasTopBar={true}>
            <Form>
              <Panel
                progressBarPercentage={(currentStep / 2) * 100}
                footer={{
                  left: currentStep === 2 && (
                    <Button
                      disabled={surveySubmitted}
                      $variant={'secondary'}
                      type='button'
                      onClick={() => {
                        formikProps.setSubmitting(false)
                        setCurrentStep(1)
                      }}
                    >
                      Back
                    </Button>
                  ),
                  right: (
                    <Button
                      $variant={'primary'}
                      type='submit'
                      disabled={cannotSubmit}
                      $loading={isSavingApi}
                      onClick={(e) => {
                        if (formikProps.isSubmitting && currentStep === 2) {
                          formikProps.setSubmitting(false)
                        }

                        if (currentStep === 2 && step2Valid && cannotSubmit) {
                          e.preventDefault()
                          onSubmit(formikProps.values, formikProps)
                        }
                      }}
                    >
                      {currentStep === 1 ? 'Continue' : 'Finish'}
                    </Button>
                  ),
                }}
                body={
                  <FormInner
                    calendlyStep={calendlyStep}
                    calendlyUrl={calendlyUrl}
                    currentStep={currentStep}
                  />
                }
              />
            </Form>
          </PageEditor>
        )
      }}
    </Formik>
  )
}
